import type { LogoPosition, SlidePoint } from '../../types/api';

/**
 * Geometry shared by the canvas editor and its drag handling.
 *
 * Every number here is a fraction of the square slide, which is the same unit
 * `text_position` and `logo_point` travel in. Nothing is measured in pixels, so
 * the stage can be any size on screen and still mean the same thing to the
 * renderer.
 */

/**
 * Mirrors of the backend render template (app/rendering/spec.py): canvas 1080,
 * safe margin 80, logo box 140, wrap width 1080 - 2*80 = 920. Change one side,
 * change the other.
 */

/**
 * Caption block width on a full-bleed layout, as a fraction of the slide
 * (920/1080). Since the renderer grew per-template columns this is one row of
 * `SLIDE_TEMPLATES` rather than the rule: the canvas takes its width from
 * `template.textBox.width`, which is narrower whenever a picture takes half the
 * slide. Kept here as the safe-area default the rest of the geometry assumes.
 */
export const CAPTION_WIDTH = 0.8519;

/** Logo box edge, as a fraction of the slide (140/1080). */
export const LOGO_SIZE = 0.1296;

/** Inset a preset logo keeps from the slide edge (80/1080). */
export const LOGO_MARGIN = 0.0741;

/**
 * How far the logo's chip is inflated past the logo box on every side
 * (20/1080, mirroring the renderer's chip padding). Decoration only: the
 * draggable box stays `LOGO_SIZE`, so the chip never moves a drag or a clamp.
 */
export const LOGO_BADGE_PADDING = 0.0185;

/** How close to the centre line a block must get before it snaps to it. */
export const SNAP_TOLERANCE = 0.02;

/** Arrow-key step, and the Shift-modified step. */
export const NUDGE_STEP = 0.01;
export const NUDGE_STEP_LARGE = 0.05;

/** Fractions cross the wire, so keep them short and exactly comparable. */
export function roundFraction(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/**
 * Keeps a coordinate on the canvas: never below 0, never far enough right or
 * down that the block's own footprint hangs off the edge.
 */
export function clampFraction(value: number, footprint: number): number {
  if (!Number.isFinite(value)) return 0;
  const limit = Math.max(0, 1 - footprint);
  return roundFraction(Math.min(Math.max(value, 0), limit));
}

/** Both null, or both the same place. Rounded, so a drag that lands back where it started is not a change. */
export function samePoint(left: SlidePoint | null, right: SlidePoint | null): boolean {
  if (left === null || right === null) return left === right;
  return (
    roundFraction(left.x) === roundFraction(right.x) &&
    roundFraction(left.y) === roundFraction(right.y)
  );
}

/**
 * Where a preset puts the logo box. Null for `none` — that preset means no
 * logo at all, which no custom point overrides.
 *
 * The horizontal edges are the caption column's, exactly as the renderer
 * anchors them (`_corner_origin`): on a full-bleed template that resolves to
 * the numbers this function always produced, and on a slot template the mark
 * sits in the column the type is in rather than floating over the picture.
 * The vertical edges stay the safe margin. Callers that know their template
 * pass its text box; without one the full-bleed column is assumed.
 */
export function presetLogoPoint(
  position: LogoPosition,
  textBox?: { left: number; width: number },
): SlidePoint | null {
  const near = LOGO_MARGIN;
  const columnLeft = textBox?.left ?? LOGO_MARGIN;
  const columnRight = textBox
    ? roundFraction(textBox.left + textBox.width - LOGO_SIZE)
    : roundFraction(1 - LOGO_MARGIN - LOGO_SIZE);
  const far = roundFraction(1 - LOGO_MARGIN - LOGO_SIZE);

  switch (position) {
    case 'top_left':
      return { x: columnLeft, y: near };
    case 'top_right':
      return { x: columnRight, y: near };
    case 'bottom_left':
      return { x: columnLeft, y: far };
    case 'bottom_right':
      return { x: columnRight, y: far };
    case 'none':
      return null;
  }
}

/** `0.42` → `42%`, for the position readout. */
export function formatFraction(value: number): string {
  return `${Math.round(value * 100)}%`;
}
